import { SlotCardProps } from '#vessel/overview'
import { useT } from 'apprise-frontend-core/intl/language'
import { TenantLabel } from 'apprise-frontend-iam/tenant/label'
import { classname } from 'apprise-ui/component/model'
import { Grid } from 'apprise-ui/grid/grid'
import { Label } from 'apprise-ui/label/label'
import { NamedProperty } from 'apprise-ui/utils/namedproperty'
import { AvlPatch } from './model'

import { RestoreIcon, RevokeIcon } from '#avl/constants'
import { AvlListEntry, useAvlList } from '#avl/list'
import { RevokePanel } from '#avl/revokepanel'
import { approveSubmission } from '#submission/oracle'
import { DateLabel } from '#utils/datelabel'
import { useL } from 'apprise-frontend-core/intl/multilang'
import { useOracle } from 'apprise-frontend-iam/authz/oracle'
import { useTenantStore } from 'apprise-frontend-iam/tenant/store'
import { UserLabel } from 'apprise-frontend-iam/user/label'
import { TagLabel } from 'apprise-frontend-tags/tag/label'
import { Button } from 'apprise-ui/button/button'
import { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import './styles.scss'



export const AvlOverview = (props: SlotCardProps<AvlPatch>) => {

    const t = useT()
    const l = useL()

    const oracle = useOracle()

    const { age } = props

    const avllistFor = useAvlList()


    const fulllist = avllistFor(age)
    const previousEntries = avllistFor(age + 1)
    const tenants = useTenantStore()


    const [selected, setSelected] = useState<AvlListEntry | undefined>(undefined)

    const showEntry = (entry: AvlListEntry) => {

        // guests don't see, iotc does, same CPC does.
        return !entry.declaration.revoked || (!oracle.isGuest() && oracle.hasNoTenant()) || oracle.tenant() === entry.declaration.tenant

    }

    return <Grid rowGap={8} columnGap={10} >

        <Grid.Row>

              {age === 0 && oracle.can(approveSubmission) && 
              <Grid.Col width={20} />
            }
            <Grid.Col width={60}>
                <NamedProperty className='avl-header' name={t('avl.year')} />
            </Grid.Col>
            <Grid.Col>
                <NamedProperty className='avl-header' name={t('avl.tenant')} />
            </Grid.Col>
            <Grid.Col align='center'>
                <NamedProperty className='avl-header' name={t('avl.species')} />
            </Grid.Col>
            <Grid.Col align='center'>
                <NamedProperty className='avl-header' name={t('avl.charterer_name')} />
            </Grid.Col>
            <Grid.Col width='1.5fr'>
                <NamedProperty className='avl-header' name={t('avl.charterer_address')} />
            </Grid.Col>

        </Grid.Row>

        {

            fulllist.filter(showEntry).map((entry, i) => {

                const previous = previousEntries.find(previous => previous.declaration.year === entry.declaration.year && previous.declaration.tenant === entry.declaration.tenant)

                const isRevoked = !!entry.declaration.revoked
                const markRevoked = isRevoked && (new Date(entry.declaration.revoked!.revokedOn).getTime() >= entry.revision.lifecycle.lastModified! || age === 0)

                // we highlight new declarations, but not if there are no possible matches.
                const latest = !!previousEntries.length && !previous

                const classes = classname(latest && 'avl-latest', markRevoked && 'avl-revoked')

                return <Grid.Row key={i} className={classes} >

                    {age === 0 && oracle.can(approveSubmission) &&

                        <Grid.Col width={20}>
                            <NamedProperty valueAlign='center' highlightMode='none' className='revoked-col' style={{ background: 'transparent' }} valueStyle={{ paddingLeft:0, paddingRight:0 }}>

                                <Button noLabel iconPlacement='left' type='ghost' size='small'
                                    icon={<RevokeIcon full={isRevoked} className={`revoked-${isRevoked}`} />}
                                    tip={isRevoked ? <RevocationData onSelect={setSelected} entry={entry} /> : t('avl.revoke_tip')}
                                    noReadonly={oracle.tenant() === entry.declaration.tenant}
                                    enabled={oracle.hasNoTenant() || (oracle.tenant() === entry.declaration.tenant)}
                                    onClick={() => { setSelected(entry) }} />

                            </NamedProperty>
                        </Grid.Col>
                    }

                    <Grid.Col width={60}>
                        <NamedProperty valueAlign='center' previously={previous?.declaration.year} currently={entry.declaration.year}>
                            <Label title={entry.declaration.year} noIcon />
                        </NamedProperty>
                    </Grid.Col>

                    <Grid.Col>
                        <NamedProperty valueAlign='left' previously={previous?.declaration.tenant} currently={entry.declaration.tenant}>
                            <TenantLabel bare noIcon tenant={entry.declaration.tenant} tip={l(tenants.safeLookup(entry.declaration.tenant).name)} />
                        </NamedProperty>
                    </Grid.Col>

                    <Grid.Col>
                        <NamedProperty valueAlign='left' previously={previous?.declaration.species} currently={entry.declaration.species}>
                            <TagLabel tipPlacement='left' unknown tag={entry.declaration.species} noIcon noLink />
                        </NamedProperty>
                    </Grid.Col>

                    <Grid.Col>
                        <NamedProperty valueAlign='left' previously={previous?.declaration.charterer.name} currently={entry.declaration.charterer.name}>
                            <Label tipPlacement='left' tip={entry.declaration.charterer.name} title={entry.declaration.charterer.name} noIcon />
                        </NamedProperty>
                    </Grid.Col>

                    <Grid.Col width='1.5fr'>
                        <NamedProperty valueAlign='left' previously={previous?.declaration.charterer.address} currently={entry.declaration.charterer.address}>
                            <Label tip={entry.declaration.charterer.address} tipPlacement='left' title={entry.declaration.charterer.address} noIcon />
                        </NamedProperty>
                    </Grid.Col>

                </Grid.Row>
            })
        }

        <RevokePanel entry={selected} onClose={() => setSelected(undefined)} />


    </Grid>
}


const RevocationData = ({ entry, onSelect }: { entry: AvlListEntry, onSelect: (_: AvlListEntry) => void }) => {

    const t = useT()

    return <Fragment>
        <div className='apprise-row'>
            <span className='revocation-prop'>{t('avl.revoke_date_short')}</span> &nbsp;  <DateLabel bare date={entry.declaration.revoked?.revokedOn} />
        </div>
        <div className='apprise-row'>
            <span className='revocation-prop'>{t('avl.revoke_user_short')}</span> &nbsp; <UserLabel bare user={entry.declaration.revoked?.revokedBy} />
        </div>
        {entry.declaration.revoked?.comment &&
            <div style={{ textAlign: 'left' }}>
                <span className='revocation-prop'>{t('avl.revoke_comment_short')}</span>: &nbsp; <span style={{ marginTop: 5, fontStyle: 'italic' }}>{entry.declaration.revoked?.comment}</span>
            </div>
        }
        <div style={{ marginTop: 10, textAlign: 'right' }}>
            <Button type='link' icon={<RestoreIcon />} iconPlacement='left' onClick={() => onSelect(entry)} noLabel size='small'>
                <span style={{ color: 'deepskyblue', fontVariant: 'small-caps' }}>{t('avl.restore_invite')}</span>
            </Button>
        </div>
    </Fragment>
}